import type { Dispatch, SetStateAction } from "react";

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import assetClient from "src/services/assetClient";

const MAXIUMUM_REFETCH_COUNT = 10;

interface useVideoAssetByMediaIdOptions {
	mediaId?: string | null;
	enabled?: boolean;
	onSuccess?: ((data: VideoData) => void) | undefined;
}

export type PlaybackType = "HlsUri" | "ProtectedHlsUri";
export type ProcessingStatus = "ReadyForPlayback" | "Processing";

export type VideoData = {
	playbackType: PlaybackType;

	processingStatus: ProcessingStatus;

	playbackUri: string;

	token: string | null;

	thumbnail: string;

	width: number;

	height: number;
};

export interface VideoProcessingStatusOptions {
	processingStatus: ProcessingStatus;

	refetchCount: number;

	refetchIntervalIndex: number;

	intervals: number[];

	setRefetchInterval: Dispatch<SetStateAction<number | false>>;

	setRefetchCount: Dispatch<SetStateAction<number>>;

	setRefetchIntervalIndex: Dispatch<SetStateAction<number>>;
}

const fetchVideoAssetByMediaId = async (mediaId?: string | null) => {
	if (!mediaId) {
		return Promise.reject("No mediaId to fetch video for");
	}
	const url = `/api/v1.0/asset/video/${mediaId}`;
	const { data } = await assetClient.get<VideoData>(url);
	return data;
};

export const handleVideoProcessingStatus = ({
	processingStatus,
	refetchCount,
	intervals,
	refetchIntervalIndex,
	setRefetchInterval,
	setRefetchCount,
	setRefetchIntervalIndex
}: VideoProcessingStatusOptions) => {
	if (processingStatus === "ReadyForPlayback") {
		setRefetchInterval(false);

		return;
	}

	if (
		processingStatus === "Processing" &&
		refetchCount === MAXIUMUM_REFETCH_COUNT
	) {
		if (refetchIntervalIndex === intervals.length - 1) {
			setRefetchInterval(false); // Stop further refetch attempts
		} else {
			setRefetchCount(1);
			setRefetchIntervalIndex((refetchIntervalIndex += 1));
			setRefetchInterval(intervals[refetchIntervalIndex]);
		}

		return;
	}

	if (processingStatus === "Processing") {
		setRefetchCount((refetchCount += 1));
	}
};

const useVideoAssetByMediaId = ({
	mediaId,
	onSuccess,
	enabled = true
}: useVideoAssetByMediaIdOptions) => {
	const intervals = [
		3000, 6000, 9000, 12000, 24000, 48000, 96000, 192000, 384000, 768000
	];

	const [refetchCount, setRefetchCount] = useState(1);

	const [refetchIntervalIndex, setRefetchIntervalIndex] = useState(0);
	const [refetchInterval, setRefetchInterval] = useState<number | false>(
		intervals[refetchIntervalIndex]
	);

	const query = useQuery(
		["video", mediaId],
		() => fetchVideoAssetByMediaId(mediaId),
		{
			refetchInterval,
			onSuccess: (data) => {
				onSuccess?.(data);

				handleVideoProcessingStatus({
					processingStatus: data.processingStatus,
					refetchCount,
					refetchIntervalIndex,
					intervals,
					setRefetchInterval,
					setRefetchCount,
					setRefetchIntervalIndex
				});
			},
			enabled
		}
	);

	return query;
};

export default useVideoAssetByMediaId;
